import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./game.css";
import { ReactComponent as WordCloudMap } from "./img/innerpeace/wordcloud.svg";
import roomDesktopImg from "./img/innerpeace/room-desktop.png";
import roomMobileImg from "./img/innerpeace/room-mobile.svg";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markGameOver,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 1;

// old/room.php's five hotspots, in the order the mind-palace story unlocks
// them (mirror -> book -> phone -> safe -> chest). The real map art only
// has exact coordinates for these on desktop (escape1.svg) and 4 of 5 on
// mobile (mobile-innerpeace-01.svg has no separate "mirror" layer, so that
// one region is an approximated placeholder).
const HOTSPOTS = ["mirror", "book", "mobile", "safe", "chest"];

// Percentages taken from escape1.svg's <image> transforms against its
// 1920x911 viewBox, which is pixel-identical to room-desktop.png.
const DESKTOP_HOTSPOT_RECT = {
  mirror: { left: 69.68, top: 3.44, width: 13.07, height: 39.87 },
  book: { left: 49.48, top: 46.12, width: 7.81, height: 5.93 },
  mobile: { left: 77.76, top: 79.4, width: 5.57, height: 4.61 },
  safe: { left: 20.8, top: 49.3, width: 8.85, height: 20.42 },
  chest: { left: 4.99, top: 39.98, width: 9.74, height: 10.43 },
};

// Percentages from mobile-innerpeace-01.svg's <image> transforms against
// its 414x896 viewBox (pixel-identical to room-mobile.svg rendered as an
// <img>). "mirror" has no dedicated <image> layer in that file (unlike
// desktop), so its rect was measured directly off the rendered background
// art instead — it's the wall mirror visible top-right, partly cropped by
// the canvas edge.
const MOBILE_HOTSPOT_RECT = {
  mirror: { left: 83, top: 36, width: 17, height: 32 },
  book: { left: 54.49, top: 58.83, width: 13.04, height: 2.23 },
  mobile: { left: 79.45, top: 74.52, width: 9.18, height: 1.79 },
  safe: { left: 14.96, top: 60.28, width: 16.67, height: 8.82 },
  chest: { left: 0.02, top: 56.28, width: 9.66, height: 5.13 },
};

// old/room.php's stageTexts (shown before each stage's puzzle opens).
const STAGE_INTRO_TEXTS = {
  1: "I appear as who you are, yet I do not define you. To unlock my secrets, find a way to your reflection.",
  2: "You have unlocked the mirror’s truth, but clarity is still clouded. Thoughts tangle like jumbled letters in a book, hiding the key to peace. Seek the pages where words await—unravel their order to quiet the storm.",
  3: "The clutter is cleared, your focus restored,Yet the screen still holds you, making you sore.Look away, take a break, just for a while,What habit helps your eyes revive?",
  4: "Three keys you hold, but choose with care,For self-love grows with actions rare. Click the vault, unlock your way,Your future self has much to say!",
  5: "The past is written, and the future awaits. Seek the chest and find four words of truth to open the gates.",
};

// old/room.php's stageEndTexts (the "Learning:" popup after each stage).
const STAGE_LEARNING_TEXTS = {
  1: "Learning: Not all self-talk is beneficial. Choosing positive affirmations helps unlock self-awareness.",
  2: "Learning: Mindfulness helps in organizing thoughts and reducing overthinking.",
  3: "Learning: Frequent breaks improve focus and well-being.",
  4: "Learning: Small actions today build long-term well-being.",
  5: "Learning: True self-worth is built on inner qualities, not external validation.",
};

// old/room.php's myModal statements (Stage 1: pick 3 empowering statements).
const STATEMENTS = [
  { id: 1, text: "I trust myself to make wise decisions" },
  { id: 2, text: "I am not smart enough to achieve my goals" },
  { id: 3, text: "If things don’t go as planned, I have failed" },
  { id: 4, text: "I choose to focus on solutions, not problems" },
  { id: 5, text: "Change is too difficult for me" },
  { id: 6, text: "I have the strength to overcome any challenge" },
];
const STATEMENTS_REQUIRED = [1, 4, 6];

// old/room.php's myModal2 "MEDITATION" anagram (Stage 2).
const ANAGRAM_QUESTIONS = [
  { id: "idea", hint: "It helps spark a creative idea", answer: "idea" },
  { id: "mind", hint: "It’s all about training the mind", answer: "mind" },
  { id: "time", hint: "Meditation allows time for self-reflection", answer: "time" },
  { id: "team", hint: "Building meaningful connections with others", answer: "team" },
];

// old/room.php's myModal3 screen-fatigue quiz (Stage 3).
const QUIZ_OPTIONS = [
  { position: 1, text: "Lower screen brightness" },
  { position: 2, text: "Take a 5-minute break every 30 minutes" },
  { position: 3, text: "Close one eye while scrolling" },
];
const QUIZ_CORRECT_POSITION = 2;

// old/room.php's myModal4 self-care wordcloud (Stage 4) svg ids -> fill class.
const WORDCLOUD_WORDS = [
  { id: "Sustainable_Living", fillClass: "cls-1" },
  { id: "Read_Regularly", fillClass: "cls-5" },
  { id: "Practice_Gratitude", fillClass: "cls-2" },
  { id: "Hydrate", fillClass: "cls-6" },
  { id: "Stay_Active", fillClass: "cls-4" },
  { id: "Prioritize_Sleep", fillClass: "cls-3" },
];
const WORDCLOUD_MIN_CLICKS_FOR_SUBMIT = 3;

// old/js/wordpuzzle.js's word list (Stage 5).
const WORDSEARCH_WORDS = ["CONFIDENT", "RESILIENT", "KIND", "WORTHY", "IGNORANT", "LAZY", "DOUBTFUL"];
const WORDSEARCH_POSITIVE_WORDS = ["CONFIDENT", "RESILIENT", "KIND", "WORTHY"];
const WORDSEARCH_GRID_SIZE = 10;

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

/**
 * old/room.php ran two parallel popup systems: a custom parchment card with
 * an "X" close icon for the per-stage story/learning beats (openStagePopup /
 * openEndStagePopup), and a SweetAlert2 popup styled the same way but with
 * an "OK" button for puzzle feedback (hints, validation, correct answers).
 * This hook reproduces both as one promise-based queue so callers can
 * `await showNotice(...)` exactly like the old code awaited Swal.fire(...).
 */
function useNoticeQueue() {
  const [notice, setNotice] = useState(null);

  const showNotice = useCallback((text, opts = {}) => {
    return new Promise((resolve) => {
      setNotice({ text, title: opts.title || "", variant: opts.variant || "ok", resolve });
    });
  }, []);

  const closeNotice = useCallback(() => {
    setNotice((n) => {
      n?.resolve();
      return null;
    });
  }, []);

  const noticeNode = notice ? (
    <div className="room-modal-backdrop">
      <div className={"room-notice-card" + (notice.variant === "x" ? " room-notice-card--wide" : "")}>
        {notice.variant === "x" && (
          <button type="button" className="room-notice-close" onClick={closeNotice} aria-label="Close">
            ✕
          </button>
        )}
        {notice.title && <div className="room-notice-title">{notice.title}</div>}
        <div className="room-notice-text">{notice.text}</div>
        {notice.variant === "ok" && (
          <button type="button" className="room-notice-ok" onClick={closeNotice}>
            OK
          </button>
        )}
      </div>
    </div>
  ) : null;

  return { showNotice, noticeNode };
}

/** A hotspot region positioned over the room art by percentage (see *_HOTSPOT_RECT above). */
function Hotspot({ rect, active, onClick, label }) {
  return (
    <button
      type="button"
      className={"room-hotspot" + (active ? " is-active" : "")}
      style={{
        left: `${rect.left}%`,
        top: `${rect.top}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`,
      }}
      onClick={onClick}
      disabled={!active}
      aria-label={label}
    />
  );
}

/** old/room.php's myModal: pick exactly 3 empowering statements. */
function StatementsPuzzle({ onDone }) {
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const toggle = useCallback(
    (id) => {
      if (submitted) return;
      setSelected((cur) => {
        if (cur.includes(id)) return cur.filter((v) => v !== id);
        if (cur.length >= 3) return cur;
        return [...cur, id];
      });
    },
    [submitted]
  );

  const handleSubmit = useCallback(() => {
    if (selected.length < 3 || submitted) return;
    setSubmitted(true);
    const correctCount = selected.filter((id) => STATEMENTS_REQUIRED.includes(id)).length;
    window.setTimeout(() => onDone(correctCount), 1200);
  }, [selected, submitted, onDone]);

  return (
    <div className="room-modal-backdrop">
      <div className="room-modal room-modal--wide">
        <h4 className="room-modal-heading">
          Select 3 statements that you must say to empower yourself
        </h4>
        <div className="room-statements-list">
          {STATEMENTS.map((s) => {
            const isSelected = selected.includes(s.id);
            const isRequired = STATEMENTS_REQUIRED.includes(s.id);
            let stateClass = "";
            if (submitted && isSelected) {
              stateClass = isRequired ? " is-correct" : " is-incorrect";
            } else if (isSelected) {
              stateClass = " is-selected";
            }
            return (
              <button
                key={s.id}
                type="button"
                className={"room-statement-btn" + stateClass}
                onClick={() => toggle(s.id)}
                disabled={submitted}
              >
                {s.text}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="room-submit-btn"
          disabled={selected.length < 3 || submitted}
          onClick={handleSubmit}
          aria-label="Submit"
        />
      </div>
    </div>
  );
}

/** old/room.php's myModal2: unscramble MEDITATION into idea/mind/time/team. */
function AnagramPuzzle({ onDone, showNotice }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(ANAGRAM_QUESTIONS.map((q) => [q.id, ""]))
  );
  const [locked, setLocked] = useState({});

  const handleChange = useCallback(
    (q) => (e) => {
      const val = e.target.value.replace(/^\s+/, "");
      setValues((v) => ({ ...v, [q.id]: val }));
      if (val.trim().toLowerCase() === q.answer.toLowerCase()) {
        setLocked((l) => ({ ...l, [q.id]: true }));
      }
    },
    []
  );

  const handleHint = useCallback(
    (q) => {
      void showNotice(q.hint.replace(new RegExp(q.answer, "i"), "_________"));
    },
    [showNotice]
  );

  const allFilled = ANAGRAM_QUESTIONS.every((q) => values[q.id].trim() !== "");

  const handleSubmit = useCallback(() => {
    if (!allFilled) {
      void showNotice("Please fill in all fields before submitting.");
      return;
    }
    let correctCount = 0;
    const correctAnswers = [];
    ANAGRAM_QUESTIONS.forEach((q) => {
      const answer = q.answer.charAt(0).toUpperCase() + q.answer.slice(1);
      correctAnswers.push(answer);
      if (values[q.id].trim().toLowerCase() === q.answer.toLowerCase()) correctCount++;
    });

    if (correctCount < ANAGRAM_QUESTIONS.length) {
      void showNotice(correctAnswers.join(", "), { title: "Correct answer" }).then(() => onDone(correctCount));
    } else {
      onDone(correctCount);
    }
  }, [allFilled, values, onDone, showNotice]);

  return (
    <div className="room-modal-backdrop">
      <div className="room-modal room-modal--wide">
        <h4 className="room-modal-heading">
          A cluttered mind is like a scrambled message. Rearrange the letters of
          &ldquo;MEDITATION&rdquo; to uncover what clears the mind.
        </h4>
        <p className="room-modal-subtext">
          (Click on i for the hint. If you&rsquo;re unsure of the answer, just make one up and move on)
        </p>
        <div className="room-anagram-grid">
          {ANAGRAM_QUESTIONS.map((q) => {
            const val = values[q.id];
            const isLocked = locked[q.id];
            return (
              <div key={q.id} className="room-anagram-item">
                <button type="button" className="room-hint-btn" onClick={() => handleHint(q)}>
                  i
                </button>
                <input
                  type="text"
                  autoComplete="off"
                  className="room-answer-input"
                  value={val}
                  onChange={handleChange(q)}
                  readOnly={isLocked}
                  style={val ? { background: isLocked ? "#6fd36f" : "#e05555", color: "#fff" } : undefined}
                />
              </div>
            );
          })}
        </div>
        <button type="button" className="room-submit-btn" onClick={handleSubmit} aria-label="Submit" />
      </div>
    </div>
  );
}

/** old/room.php's myModal3: best way to reduce screen fatigue. */
function QuizPuzzle({ onDone }) {
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(() => {
    if (selected == null || submitted) return;
    setSubmitted(true);
    const isCorrect = selected === QUIZ_CORRECT_POSITION;
    window.setTimeout(() => onDone(isCorrect ? 1 : 0), isCorrect ? 900 : 1600);
  }, [selected, submitted, onDone]);

  return (
    <div className="room-modal-backdrop">
      <div className="room-modal room-modal--wide">
        <h4 className="room-modal-heading">
          What is the scientifically proven best way to reduce screen fatigue?
        </h4>
        <div className="room-quiz-options">
          {QUIZ_OPTIONS.map((o) => {
            let stateClass = "";
            if (selected === o.position) stateClass = " is-selected";
            if (submitted) {
              if (o.position === selected) {
                stateClass = o.position === QUIZ_CORRECT_POSITION ? " is-correct" : " is-incorrect";
              } else if (o.position === QUIZ_CORRECT_POSITION) {
                stateClass = " is-correct-reveal";
              }
            }
            return (
              <button
                key={o.position}
                type="button"
                className={"room-quiz-option" + stateClass}
                disabled={submitted}
                onClick={() => setSelected(o.position)}
              >
                {o.text}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="room-submit-btn"
          disabled={selected == null || submitted}
          onClick={handleSubmit}
          aria-label="Submit"
        />
      </div>
    </div>
  );
}

/** old/room.php's myModal4: click self-care actions in the wordcloud svg. */
function WordCloudPuzzle({ onDone }) {
  const rootRef = useRef(null);
  const [clicked, setClicked] = useState({});
  const clickCount = Object.keys(clicked).length;

  const handleClick = useCallback(
    (e) => {
      const root = rootRef.current;
      if (!root) return;
      const word = WORDCLOUD_WORDS.find((w) => e.target.closest(`#${CSS.escape(w.id)}`));
      if (!word || clicked[word.id]) return;

      const els = root.querySelectorAll(`.${word.fillClass}`);
      els.forEach((el) => {
        el.style.fill = "green";
      });
      setClicked((c) => ({ ...c, [word.id]: true }));
    },
    [clicked]
  );

  const handleSubmit = useCallback(() => onDone(clickCount), [clickCount, onDone]);

  return (
    <div className="room-modal-backdrop">
      <div className="room-modal room-modal--wide">
        <h4 className="room-modal-heading">
          Select self-care actions that you can commit to support your dreams
        </h4>
        <div className="room-wordcloud-wrap" ref={rootRef} onClick={handleClick}>
          <WordCloudMap className="room-wordcloud-svg" />
        </div>
        <button
          type="button"
          className="room-submit-btn"
          disabled={clickCount < WORDCLOUD_MIN_CLICKS_FOR_SUBMIT}
          onClick={handleSubmit}
          aria-label="Submit"
        />
      </div>
    </div>
  );
}

/** Deterministic-enough (attempt-limited random) grid builder, ported from old/js/wordpuzzle.js. */
function buildWordSearchGrid() {
  const size = WORDSEARCH_GRID_SIZE;
  const grid = Array.from({ length: size }, () => Array(size).fill(""));

  const canPlace = (word, row, col, dir) => {
    if (dir === 0) {
      if (col + word.length > size) return false;
      for (let i = 0; i < word.length; i++) if (grid[row][col + i] !== "") return false;
    } else if (dir === 1) {
      if (row + word.length > size) return false;
      for (let i = 0; i < word.length; i++) if (grid[row + i][col] !== "") return false;
    } else {
      if (row + word.length > size || col + word.length > size) return false;
      for (let i = 0; i < word.length; i++) if (grid[row + i][col + i] !== "") return false;
    }
    return true;
  };

  const place = (word, row, col, dir) => {
    for (let i = 0; i < word.length; i++) {
      if (dir === 0) grid[row][col + i] = word[i];
      else if (dir === 1) grid[row + i][col] = word[i];
      else grid[row + i][col + i] = word[i];
    }
  };

  WORDSEARCH_WORDS.forEach((word) => {
    let placed = false;
    let attempts = 0;
    while (!placed && attempts < 200) {
      const dir = Math.floor(Math.random() * 3);
      const row = Math.floor(Math.random() * size);
      const col = Math.floor(Math.random() * size);
      if (canPlace(word, row, col, dir)) {
        place(word, row, col, dir);
        placed = true;
      }
      attempts++;
    }
  });

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (grid[row][col] === "") {
        grid[row][col] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
      }
    }
  }

  return grid;
}

/** old/room.php's myModal5 + js/wordpuzzle.js: drag-select positive traits in a letter grid. */
function WordSearchPuzzle({ onDone }) {
  const [grid] = useState(buildWordSearchGrid);
  const [selecting, setSelecting] = useState(false);
  const [path, setPath] = useState([]); // [{row,col}]
  const [found, setFound] = useState({}); // word -> cell key[]
  const [matchedWords, setMatchedWords] = useState([]);
  const startCellRef = useRef(null);
  const gridRef = useRef(null);

  const foundCellKeys = useMemo(() => {
    const set = new Set();
    Object.values(found).forEach((cells) => cells.forEach((k) => set.add(k)));
    return set;
  }, [found]);

  const pathCellKeys = useMemo(() => new Set(path.map((c) => `${c.row}-${c.col}`)), [path]);

  const buildPath = useCallback((start, end) => {
    const dx = end.col - start.col;
    const dy = end.row - start.row;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    if (!((adx === ady || dx === 0 || dy === 0) && dx >= 0 && dy >= 0)) return [start];
    const stepX = dx === 0 ? 0 : dx / adx;
    const stepY = dy === 0 ? 0 : dy / ady;
    const cells = [];
    const steps = Math.max(adx, ady);
    for (let i = 0; i <= steps; i++) {
      cells.push({ row: start.row + i * stepY, col: start.col + i * stepX });
    }
    return cells;
  }, []);

  const finishSelection = useCallback(() => {
    if (!selecting) return;
    setSelecting(false);
    const cells = path;
    if (cells.length < 2) {
      setPath([]);
      return;
    }
    const word = cells.map((c) => grid[c.row][c.col]).join("");
    const reversed = word.split("").reverse().join("");

    const match = WORDSEARCH_POSITIVE_WORDS.includes(word)
      ? word
      : WORDSEARCH_POSITIVE_WORDS.includes(reversed)
      ? reversed
      : null;

    if (match && !found[match]) {
      const keys = cells.map((c) => `${c.row}-${c.col}`);
      setFound((f) => ({ ...f, [match]: keys }));
      setMatchedWords((m) => [...m, match]);
    }
    setPath([]);
  }, [selecting, path, grid, found]);

  const cellFromPoint = useCallback((x, y) => {
    const el = document.elementFromPoint(x, y);
    const td = el?.closest("[data-row]");
    if (!td) return null;
    return { row: Number(td.dataset.row), col: Number(td.dataset.col) };
  }, []);

  const handlePointerDown = useCallback((row, col) => {
    startCellRef.current = { row, col };
    setSelecting(true);
    setPath([{ row, col }]);
  }, []);

  const handlePointerEnter = useCallback(
    (row, col) => {
      if (!selecting || !startCellRef.current) return;
      setPath(buildPath(startCellRef.current, { row, col }));
    },
    [selecting, buildPath]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!selecting) return;
      e.preventDefault();
      const touch = e.touches[0];
      const cell = cellFromPoint(touch.clientX, touch.clientY);
      if (cell && startCellRef.current) setPath(buildPath(startCellRef.current, cell));
    },
    [selecting, cellFromPoint, buildPath]
  );

  useEffect(() => {
    if (!selecting) return undefined;
    const up = () => finishSelection();
    document.addEventListener("mouseup", up);
    return () => document.removeEventListener("mouseup", up);
  }, [selecting, finishSelection]);

  const foundCount = Object.keys(found).length;

  return (
    <div className="room-modal-backdrop">
      <div className="room-modal room-modal--wide">
        <h4 className="room-modal-heading">
          Choose positive traits from the word puzzle that creates positive impact
        </h4>
        <p className="room-modal-subtext">(Kindly find minimum one word from the puzzle)</p>
        <div className="room-wordsearch-layout">
          <table
            className="room-wordsearch-grid"
            ref={gridRef}
            onTouchStart={(e) => {
              e.preventDefault();
              const touch = e.touches[0];
              const cell = cellFromPoint(touch.clientX, touch.clientY);
              if (cell) handlePointerDown(cell.row, cell.col);
            }}
            onTouchMove={handleTouchMove}
            onTouchEnd={finishSelection}
          >
            <tbody>
              {grid.map((rowArr, row) => (
                <tr key={row}>
                  {rowArr.map((letter, col) => {
                    const key = `${row}-${col}`;
                    const isFound = foundCellKeys.has(key);
                    const isPath = pathCellKeys.has(key);
                    return (
                      <td
                        key={key}
                        data-row={row}
                        data-col={col}
                        className={"room-wordsearch-cell" + (isFound ? " is-found" : isPath ? " is-path" : "")}
                        onMouseDown={() => handlePointerDown(row, col)}
                        onMouseEnter={() => handlePointerEnter(row, col)}
                      >
                        {letter}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="room-wordsearch-found">
            <h5>Words will appear here</h5>
            {matchedWords.length === 0 ? (
              <p className="room-modal-subtext">Drag across letters to find a word.</p>
            ) : (
              matchedWords.map((w) => (
                <input key={w} type="text" readOnly value={w} className="room-wordsearch-found-input" />
              ))
            )}
          </div>
        </div>
        {foundCount >= 1 && (
          <button
            type="button"
            className="room-submit-btn"
            onClick={() => onDone(foundCount)}
            aria-label="Submit"
          />
        )}
      </div>
    </div>
  );
}

export default function Game() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(() => sessionStorage.getItem(STAGE_ROW_KEY) || null);
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [points, setPoints] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [miniStage, setMiniStage] = useState(1); // 1..5, which hotspot/puzzle is live
  const [revealed, setRevealed] = useState(() => new Set([1]));
  const [activeModal, setActiveModal] = useState(null); // null | HOTSPOTS[miniStage-1]

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const introShownForRef = useRef(0);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const { showNotice, noticeNode } = useNoticeQueue();

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add("game-lock");
    body.classList.remove("common-bg");
    body.style.backgroundImage = "none";
    body.style.backgroundAttachment = "initial";
    return () => {
      html.classList.remove("game-lock");
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken || !uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ userId: uid, sessionId, organizationId, stageId: rowId }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage1 = Number(st.stage1) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage1);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  // old/room.php's openStagePopup(N) — shown before the player may click each stage's hotspot.
  useEffect(() => {
    if (!dbReady) return;
    if (introShownForRef.current === miniStage) return;
    introShownForRef.current = miniStage;
    void showNotice(STAGE_INTRO_TEXTS[miniStage], { variant: "x" });
  }, [dbReady, miniStage, showNotice]);

  const finishStage = useCallback(
    async (finalPoints) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + finalPoints;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: finalPoints,
          time: timeStr,
          total_score: finalTotal,
        });

        // This is the only stage, so finishing it finishes the whole game.
        await markGameOver({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          time: timeStr,
          total_score: finalTotal,
        });
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (isDemoBypass) {
        void showNotice("Thank you for playing. Subscribe to any PLAN to play with your peers.").then(() => {
          window.location.href = String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        });
        return;
      }

      // replace: true so the finished /game entry isn't left in history —
      // browser back from /thankyou would otherwise land back on it and
      // immediately bounce forward again via the gameover redirect above.
      navigate("/thankyou", { replace: true });
    },
    [submitting, storedUser, isDemoBypass, backendBase, adminToken, stageRowId, navigate, showNotice]
  );

  const handleHotspotClick = useCallback(
    (stageIndex) => {
      const stageNum = stageIndex + 1;
      if (stageNum !== miniStage || !revealed.has(stageNum)) return;
      setActiveModal(HOTSPOTS[stageIndex]);
    },
    [miniStage, revealed]
  );

  /** Shared close-out for every puzzle: award points, show the "Learning" popup, advance (or finish). */
  const advanceStage = useCallback(
    (earnedPoints) => {
      setPoints((p) => p + earnedPoints);
      setActiveModal(null);
      const finishedStage = miniStage;

      void showNotice(STAGE_LEARNING_TEXTS[finishedStage], { variant: "x" }).then(() => {
        if (finishedStage >= 5) {
          finishStage(points + earnedPoints);
          return;
        }
        const next = finishedStage + 1;
        setRevealed((r) => new Set(r).add(next));
        setMiniStage(next);
      });
    },
    [miniStage, points, finishStage, showNotice]
  );

  const hotspotRects = useMemo(
    () => ({
      desktop: DESKTOP_HOTSPOT_RECT,
      mobile: MOBILE_HOTSPOT_RECT,
    }),
    []
  );

  if (!dbReady) {
    return <div className="room-loading">Loading game…</div>;
  }

  return (
    <div className="room-page">
      <div className="room-timer-badge">Time: {formatSecondsToClock(elapsedSec)}</div>
      <div className="room-score-badge">Points: {points}</div>

      <div className="room-map-wrap">
        <img src={roomDesktopImg} className="room-map-img room-map-img--desktop" alt="" />
        <img src={roomMobileImg} className="room-map-img room-map-img--mobile" alt="" />

        <div className="room-hotspot-layer room-hotspot-layer--desktop">
          {HOTSPOTS.map((key, i) => (
            <Hotspot
              key={key}
              rect={hotspotRects.desktop[key]}
              active={revealed.has(i + 1) && miniStage === i + 1}
              onClick={() => handleHotspotClick(i)}
              label={`Stage ${i + 1}`}
            />
          ))}
        </div>
        <div className="room-hotspot-layer room-hotspot-layer--mobile">
          {HOTSPOTS.map((key, i) => (
            <Hotspot
              key={key}
              rect={hotspotRects.mobile[key]}
              active={revealed.has(i + 1) && miniStage === i + 1}
              onClick={() => handleHotspotClick(i)}
              label={`Stage ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {activeModal === "mirror" && <StatementsPuzzle onDone={advanceStage} />}
      {activeModal === "book" && <AnagramPuzzle onDone={advanceStage} showNotice={showNotice} />}
      {activeModal === "mobile" && <QuizPuzzle onDone={advanceStage} />}
      {activeModal === "safe" && <WordCloudPuzzle onDone={advanceStage} />}
      {activeModal === "chest" && <WordSearchPuzzle onDone={advanceStage} />}

      {noticeNode}
    </div>
  );
}
